import React, { useState } from 'react';
import { FaBrain, FaChartLine, FaClock, FaRobot, FaBook } from 'react-icons/fa';
import { MdOfflinePin } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

const features = [
    {
        icon: <MdOfflinePin />,
        title: 'Tamamen Offline',
        description: 'İnternet bağlantısına ihtiyaç duymadan her yerde çalışın.',
        details: [
            'Tüm yaptığınız yanlışlar cihazınızda depolanır',
            'İnternet kotanızı harcamaz',
            'Metroda, otobüste, her yerde tekrar edebilirsiniz',
        ]
    },
    {
        icon: <FaBrain />,
        title: 'Akıllı Tekrar',
        description: 'Spaced Repetition sistemi ile bilgileri kalıcı hafızaya atın.',
        details: [
            'Unutma eğrisine göre optimize edilmiş tekrarlar',
            'Kişiselleştirilmiş tekrar algoritması',
            'Zorlandığınız konulara odaklanma',
            'Kalıcı öğrenme garantisi'
        ]
    },
    {
        icon: <FaChartLine />,
        title: 'Detaylı Analiz',
        description: 'Gelişmiş grafiklerle ilerlemenizi ve eksiklerinizi takip edin.',
        details: [
            'Konu bazlı başarı grafikleri',
            'Haftalık ve aylık ilerleme raporları',
            'Eksik kazanımların tespiti'
        ]
    },
    {
        icon: <FaClock />,
        title: 'Pomodoro',
        description: 'Odaklanma sürenizi artırmak için yerleşik çalışma zamanlayıcısı.',
        details: [
            'Özelleştirilebilir çalışma/mola süreleri',
            'Odaklanma istatistikleri',
            'Çalışma serisi takibi'
        ]
    },
    {
        icon: <FaRobot />,
        title: 'Yapay Zeka Asistanı',
        description: 'Sorularınız için 7/24 yanınızda olan akıllı çalışma arkadaşı.',
        details: [
            'Günlük mantığı ile güm içinde yaptıklarınızı unutturmaz',
            'Takıldığınız konularda YouTube aracılığıyla yardım desteği',
            '(İleriki güncellemelerde) Kişiye özel program desteği'
        ]
    },
    {
        icon: <FaBook />,
        title: 'Konu Takibi',
        description: 'Çalışmalarınızı konu bazında takip edin.',
        details: [
            'TYT ve AYT güncel müfredat takibi',
            'Bitirilen konuları işaretleme',
            'İlerleme yüzdesi görüntüleme',
        ]
    }
];

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 }
    }
};

const Features = () => {
    const [hoveredIndex, setHoveredIndex] = useState(null);

    return (
        <section className="section features-section" id="features">
            <div className="container">
                <div className="section-header">
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        Neden Soruly?
                    </motion.h2>
                    <motion.p
                        className="section-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        Sınav sürecinde ihtiyacınız olan tüm araçlar tek bir uygulamada.
                    </motion.p>
                </div>
                <motion.div
                    className="features-grid"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    {features.map((feature, index) => (
                        <motion.div
                            className="feature-card"
                            key={index}
                            variants={itemVariants}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            whileHover={{ y: -5 }} // Reduced movement to prevent jitter with popup
                        >
                            <div className="feature-icon">
                                {feature.icon}
                            </div>
                            <h3 className="feature-title">{feature.title}</h3>
                            <p className="feature-description">
                                {feature.description}
                            </p>

                            <AnimatePresence>
                                {hoveredIndex === index && (
                                    <motion.div
                                        className="feature-detail-window"
                                        initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <div className="feature-detail-header">
                                            <div className="window-dots">
                                                <span></span><span></span><span></span>
                                            </div>
                                            <h4>{feature.title}</h4>
                                        </div>
                                        <ul className="feature-detail-list">
                                            {feature.details.map((detail, i) => (
                                                <li key={i}>{detail}</li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default Features;
